import React, { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import {
  Button,
  Dialog,
  Grid,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { FC } from "react";
import AppointmentHeader from "./printRxHeader";
import AppointmentDetails from "./printRxTable";
import useStore from "../../helpers/useStore";
import { AppointmentContext } from "../../stores/Appointment/appointment.provider";
import { AppointmentDto, CalculateRxDTO } from "../../api-client";
import { RxStoreContext } from "../../stores/Rx/rx.provider";
import notificationStore from "../../stores/Notification/notificationStore";
import { useRecordInformingPrintout } from "../../stores/Appointment/appointment.mutation";
import { observer } from "mobx-react";
import { buildAppointmentStartRows, buildRxPrintoutRows } from "./printoutRows";

interface DialogBoxAppointmentProps {
  id: any;
  openDialog: boolean;
  handleOnClose: () => void;
  single: boolean;
  tarifId: string | undefined | null;
  rxDate: string | undefined | null;
}

const DialogBoxAppointment: FC<DialogBoxAppointmentProps> =observer( ({
  id,
  openDialog,
  handleOnClose,
  single = true,
  tarifId,
  rxDate,
}) => {
  const dialogRef = useRef<any>(null);
  // const [selectedValue, setSelectedValue] = useState('No Office');
  const AppointmentStore = useStore(AppointmentContext);
  const RxStore = useStore(RxStoreContext);
  const { getRxPriceById } = RxStore;
  const { getAppointmentsForPatientWithRxId, getAppointmentsForPatient,getAppointmentsPrintOut } = AppointmentStore;
  const [singleRx, setSingleRx] = useState<AppointmentDto[]>([]);
  const [rxPrice, setRxPrice] = useState<CalculateRxDTO>();
  const [accessUrl, setAccessUrl] = useState<string>();
  const [actionLoading, setActionLoading] = useState(false);
  const { mutateAsync: recordInformingPrintout } = useRecordInformingPrintout();

    const doPrint = async () => {
        try {
          const response:any=await getAppointmentsPrintOut(id,single)
          setAccessUrl(response?.accessUrl);
          if (response?.accessUrl) {
            window.open(response.accessUrl, "_blank");
          }
          handleOnClose()
        } catch (err) {
          notificationStore.showMessage("Fehler beim Herunterladen des Dokuments", "error", err);
        }

    };

  const [patientDetails, setPatientDetails] = useState({
    firstName: "",
    lastName: "",
  });

  useEffect(() => {
    const fetchData = async () => {      
      try {
        const priceData = await getRxPriceById(id);
        setRxPrice(priceData);
      } catch (error) {
        console.error("Fetching error: ", error);
      }
    };

    if (openDialog && single) {
      fetchData();
    }
  }, [getRxPriceById, id, openDialog, single]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchFunction = single ? getAppointmentsForPatientWithRxId : getAppointmentsForPatient;
        const appointmentData = await fetchFunction(id);

        setSingleRx(appointmentData);

        const { firstName, lastName } = appointmentData[0].timeSlots[0].therapyRx.rx.patient;
        setPatientDetails({
          firstName,
          lastName,
        });
      } catch (error) {
        console.error("Fetching error: ", error);
      }
    };

    if (openDialog) {
      fetchData();
    }
  }, [id, single, getAppointmentsForPatientWithRxId, getAppointmentsForPatient, openDialog]);

  // For an Rx the dialog previews what the PDF will contain: the start times of that Rx's
  // time slots, back to back ones merged. The appointment start is not usable here, it is the
  // earliest of all slots and may belong to another Rx or another point of the day.
  // The rows come out in ascending order, so the old reverse() of the DESC list is gone.
  const printRows = single
    ? buildRxPrintoutRows(singleRx)
    : buildAppointmentStartRows(singleRx);

  const roundToNearestHundreth = (num: number): number => {
    return Math.ceil(num * 100) / 100;
  };

  const isValid = rxPrice?.matchNumberofAppointments;
  let totalPrice;
  if (isValid) {
    totalPrice = rxPrice.realPrice;
  } else {
    totalPrice = rxPrice?.estimatedPrice;
  }
  totalPrice = roundToNearestHundreth(totalPrice!);

  // Collect the scheduled timeslots that are about to be printed, so they can
  // be marked as informed (printing implies the patient is being handed times).
  //
  // release/0.4 read this off `filteredRx`, which MD-365 removed from the 0.3
  // line when it replaced the appointment-start render path with `printRows`.
  // The same selection is kept here rather than widened to every appointment in
  // `singleRx`: it is the narrower set, so the merge cannot start marking slots
  // of a startless (cancelled) appointment as informed.
  const printedAppointments = singleRx.filter((appointment) => appointment.start);

  const collectPrintedTimeSlotIds = (): string[] =>
    printedAppointments
      .flatMap((a) => a.timeSlots || [])
      .filter((ts: any) => ts?.id && ts?.start)
      .map((ts: any) => String(ts.id));

  // Printing implies the patient is being handed their appointment times, so
  // record the informing automatically before opening the printout.
  const handlePrintClick = async () => {
    setActionLoading(true);
    try {
      const ids = collectPrintedTimeSlotIds();
      if (ids.length > 0) {
        try {
          await recordInformingPrintout({ timeSlotIds: ids });
        } catch (err) {
          notificationStore.showMessage("Fehler beim Markieren als informiert", "error", err);
        }
      }
      await doPrint();
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div>
      <Dialog
        open={openDialog}
        onClose={handleOnClose}
      >


        <DialogContent ref={dialogRef}>
          <div>
            <AppointmentHeader
              patientDetails={patientDetails}
              rxDate={rxDate}
              single={single}
              price={totalPrice}
              tarifId={tarifId}
              isProvisionalPrice={rxPrice?.isProvisionalPrice}
            />
            <AppointmentDetails appointments={printRows} />
          </div>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleOnClose} variant="text" disabled={actionLoading}>Abbrechen</Button>
            <Button
              variant="contained"
              onClick={handlePrintClick}
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {actionLoading ? "Wird gedruckt..." : "Drucken"}
            </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
});

export default DialogBoxAppointment;
